import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTag, faTimes } from '@fortawesome/free-solid-svg-icons';
import formattedMoney from '../../utils/formatMoney';

const PERCENT_PRESETS = [5, 10, 15, 20];

/**
 * Modal cấu hình ngưỡng alert theo dõi giá.
 * Hai chế độ: giảm theo % hoặc giá mục tiêu cụ thể.
 */
function PriceWatchAlertModal({ visible, currentPrice, onConfirm, onCancel, confirmLabel = 'Theo dõi' }) {
    const [mode, setMode] = useState('percent');
    const [selectedPercent, setSelectedPercent] = useState(10);
    const [targetInput, setTargetInput] = useState('');

    const targetParsed = parseInt(targetInput.replace(/\D/g, ''), 10);
    const targetValid = mode === 'target' ? targetParsed > 0 && targetParsed < currentPrice : true;

    const previewPrice = mode === 'percent'
        ? Math.round(currentPrice * (1 - selectedPercent / 100))
        : null;

    const handleConfirm = () => {
        if (mode === 'percent') {
            onConfirm({ alertThreshold: selectedPercent, alertTargetPrice: null });
        } else {
            if (!targetValid) return;
            onConfirm({ alertThreshold: 5, alertTargetPrice: targetParsed });
        }
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onCancel}>
            <div className="absolute inset-0 bg-black/50" />
            <div
                className="relative bg-surface rounded-t-2xl w-full max-w-md p-5 pb-8 flex flex-col gap-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag handle */}
                <div className="w-9 h-1 bg-surface-raised rounded-full mx-auto -mt-1" />

                {/* Header */}
                <div className="flex items-center gap-3 pb-3 border-b border-line">
                    <div className="w-11 h-11 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faBell} className="text-primary-500 text-lg" />
                    </div>
                    <div>
                        <p className="font-bold text-sm">Đặt thông báo giá</p>
                        <p className="text-xs text-content-3">
                            Hiện tại:{' '}
                            <span className="font-semibold text-content-2">{formattedMoney.format(currentPrice)}</span>
                        </p>
                    </div>
                    <button onClick={onCancel} className="ml-auto p-1 text-content-3 hover:text-content-2">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Segmented tabs */}
                <div className="flex bg-surface-muted rounded-xl p-1 gap-1">
                    {[['percent', 'Giảm theo %'], ['target', 'Giá cụ thể']].map(([val, label]) => (
                        <button
                            key={val}
                            onClick={() => setMode(val)}
                            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${
                                mode === val
                                    ? 'bg-primary-500 text-white shadow'
                                    : 'text-content-3 hover:text-content-2'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Percent presets */}
                {mode === 'percent' && (
                    <div className="grid grid-cols-2 gap-2">
                        {PERCENT_PRESETS.map((p) => {
                            const active = selectedPercent === p;
                            return (
                                <button
                                    key={p}
                                    onClick={() => setSelectedPercent(p)}
                                    className={`flex flex-col items-center py-3 rounded-xl border-2 transition-all ${
                                        active
                                            ? 'border-primary-500 bg-primary-50'
                                            : 'border-line bg-surface-sunken hover:border-line-strong'
                                    }`}
                                >
                                    <span className={`text-xl font-bold ${active ? 'text-primary-500' : 'text-content'}`}>
                                        −{p}%
                                    </span>
                                    <span className={`text-xs mt-0.5 ${active ? 'text-primary-400' : 'text-content-3'}`}>
                                        {formattedMoney.format(Math.round(currentPrice * (1 - p / 100)))}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Target price input */}
                {mode === 'target' && (
                    <div className="flex flex-col gap-1.5">
                        <div className={`flex items-center gap-2 border-2 rounded-xl px-3 h-12 bg-surface-sunken ${
                            targetInput && !targetValid ? 'border-red-400' : 'border-line focus-within:border-primary-400'
                        }`}>
                            <FontAwesomeIcon icon={faTag} className="text-content-3 text-sm" />
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="Nhập giá mục tiêu"
                                value={targetInput}
                                onChange={(e) => setTargetInput(e.target.value)}
                                className="flex-1 bg-transparent text-sm outline-none"
                                autoFocus
                            />
                            <span className="text-xs text-content-3">đ</span>
                        </div>
                        {targetInput && !targetValid && (
                            <p className="text-xs text-red-500 px-1">
                                Giá phải thấp hơn giá hiện tại ({formattedMoney.format(currentPrice)})
                            </p>
                        )}
                    </div>
                )}

                {/* Preview pill */}
                {(mode === 'percent' || (mode === 'target' && targetValid && targetInput)) && (
                    <div className="flex items-center gap-2 bg-primary-50 border border-orange-200 rounded-lg px-3 py-2">
                        <FontAwesomeIcon icon={faBell} className="text-primary-500 text-xs flex-shrink-0" />
                        <span className="text-xs text-primary-600">
                            {mode === 'percent'
                                ? `Thông báo khi giá ≤ ${formattedMoney.format(previewPrice)} (−${selectedPercent}%)`
                                : `Thông báo khi giá ≤ ${formattedMoney.format(targetParsed)}`}
                        </span>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-1">
                    <button
                        onClick={onCancel}
                        className="flex-1 py-3 border border-line rounded-xl text-sm font-semibold text-content-2 hover:bg-surface-sunken transition-colors"
                    >
                        Huỷ
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!targetValid}
                        className="flex-1 py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PriceWatchAlertModal;
